/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    PutAwayLinesService,
    PutAwayListsService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { PutAwayListStore } from './put-away-list.store';
import {
    PutAwayLineRead,
    PutAwayListCreate,
    PutAwayListRead,
    PutAwayListSearchParams,
} from '../../entities/put-away-list.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class PutAwayListDataService extends AbstractDataService<
    PutAwayListRead,
    PutAwayListCreate
> {
    protected EntityName = PFWmsEntities.PutAwayList;

    constructor(
        private service: PutAwayListsService,
        private linesService: PutAwayLinesService,
        store: PutAwayListStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }

    private withLines$(list: PutAwayListRead): Observable<PutAwayListRead> {
        return this.linesService
            .putAwaySearch({
                putAwayListId: list.id!,
                pageNumber: 1,
                pageSize: 50,
            })
            .pipe(
                map(response => ({ ...list, putAways: response.data ?? [] }))
            );
    }

    protected createObs$(body: PutAwayListCreate): Observable<PutAwayListRead> {
        return (
            this.service.putAwayListCreate({
                putAwayListViewModelCreateBody: body,
            }) as Observable<PutAwayListRead>
        ).pipe(switchMap(list => this.withLines$(list)));
    }

    protected deleteObs$(id: string): Observable<PutAwayListRead> {
        return (
            this.service.putAwayListDelete({
                id,
            }) as Observable<PutAwayListRead>
        ).pipe(switchMap(list => this.withLines$(list)));
    }

    protected undoDeleteObs$(id: string): Observable<PutAwayListRead> {
        return (
            this.service.putAwayListUndoDelete({
                id,
            }) as Observable<PutAwayListRead>
        ).pipe(switchMap(list => this.withLines$(list)));
    }

    protected getObs$(id: string): Observable<PutAwayListRead> {
        return (
            this.service.putAwayListReadById({
                id,
            }) as Observable<PutAwayListRead>
        ).pipe(switchMap(list => this.withLines$(list)));
    }

    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<PutAwayListRead> {
        return (
            this.service.putAwayListUpdatePatch({
                id,
                operation: operations,
            }) as Observable<PutAwayListRead>
        ).pipe(switchMap(list => this.withLines$(list)));
    }

    protected searchObs$(
        searchParams: PutAwayListSearchParams
    ): Observable<IPagedResult<PutAwayListRead>> {
        return (
            this.service.putAwayListSearch(searchParams) as Observable<
                IPagedResult<PutAwayListRead>
            >
        ).pipe(
            switchMap(result =>
                result.data.length
                    ? forkJoin(
                          result.data.map(list => this.withLines$(list))
                      ).pipe(map(lists => ({ ...result, data: lists })))
                    : [result]
            )
        );
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<PutAwayListRead>>> {
        return this.service.putAwayListGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<PutAwayListRead>>
        >;
    }

    completePutAwayLine$(
        putAwayLineId: string,
        containerId?: string
    ): Observable<PutAwayLineRead> {
        return this.linesService.v1PutAwayLinesPutAwayLineIdCompletePost({
            putAwayLineId,
            putAwayLineCompleteRequest: {
                containerId: containerId ?? null,
            },
        }) as Observable<PutAwayLineRead>;
    }

    claimPutAwayLists$(warehouseId: string, putAwayListIds: string[]) {
        return this.service.v1PutAwayListsClaimPost({
            putAwayListClaimRequest: {
                warehouseId,
                putAwayListIds,
            },
        }) as Observable<PutAwayListRead[]>;
    }

    unClaimPutAwayLists$(warehouseId: string, putAwayListIds: string[]) {
        return this.service.v1PutAwayListsUnclaimPost({
            putAwayListUnclaimRequest: {
                warehouseId,
                putAwayListIds,
            },
        });
    }
}
